import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Theme store -- manages dark/light/system theme preference.
 *
 * Dark is the DEFAULT theme (no class on html).
 * Light is activated by adding 'light' class to html.
 * System mode follows prefers-color-scheme and updates dynamically.
 *
 * Persisted to localStorage under key 'opus-ia-theme'.
 */

export type ThemePreference = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

interface ThemeState {
  /** User's explicit preference */
  theme: ThemePreference;
  /** Resolved theme after system detection */
  resolvedTheme: ResolvedTheme;

  /** Set theme to a specific value */
  setTheme: (theme: ThemePreference) => void;
  /** Cycle through: dark -> light -> system -> dark */
  toggleTheme: () => void;
  /** Internal: update resolved theme when system preference changes */
  _setResolvedTheme: (resolved: ResolvedTheme) => void;
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return getSystemTheme();
  return preference;
}

const CYCLE_ORDER: ThemePreference[] = ['dark', 'light', 'system'];

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark' as ThemePreference,
      resolvedTheme: 'dark' as ResolvedTheme,

      setTheme: (theme: ThemePreference) => {
        set({ theme, resolvedTheme: resolveTheme(theme) });
      },

      toggleTheme: () => {
        const current = get().theme;
        const idx = CYCLE_ORDER.indexOf(current);
        const next: ThemePreference =
          CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length] ?? 'dark';
        set({ theme: next, resolvedTheme: resolveTheme(next) });
      },

      _setResolvedTheme: (resolved: ResolvedTheme) => {
        set({ resolvedTheme: resolved });
      },
    }),
    {
      name: 'opus-ia-theme',
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => {
        return (rehydrated?: ThemeState) => {
          if (rehydrated) {
            rehydrated.resolvedTheme = resolveTheme(rehydrated.theme);
          }
        };
      },
    },
  ),
);
